import type { PageKey, SiteContent, DomainRow, DnsRecord, SiteBundle } from "./types";

/* ---- immutable path get/set ("styles.items.2.name") ---- */
export type Path = string | (string | number)[];
const parts = (p: Path) => (Array.isArray(p) ? p : String(p).split(".").filter((x) => x !== "").map((k) => (/^\d+$/.test(k) ? Number(k) : k)));
export function getIn(obj: any, p: Path): any {
  return parts(p).reduce((a: any, k) => (a == null ? undefined : a[k as any]), obj);
}
export function setIn<T>(obj: T, p: Path, value: any): T {
  const ks = parts(p);
  const rec = (o: any, i: number): any => {
    const k = ks[i];
    const base = o == null ? (typeof k === "number" ? [] : {}) : o;
    const next = i === ks.length - 1 ? value : rec(base[k as any], i + 1);
    if (Array.isArray(base)) { const c = base.slice(); c[k as number] = next; return c; }
    const c: any = { ...base };
    if (next === undefined) delete c[k as string]; else c[k as string] = next;
    return c;
  };
  return ks.length ? rec(obj, 0) : value;
}

/* key-order-proof JSON — Postgres jsonb reorders keys, so a plain stringify
   would call two identical documents different */
/* Cached per object: edits are immutable path updates, so untouched subtrees
   keep their identity and a keystroke only re-serializes the path it changed.
   (Never mutate a document in place after it has been compared.) */
const STABLE_CACHE = new WeakMap<object, string>();
export function stable(v: any): string {
  if (v === null || typeof v !== "object") return JSON.stringify(v === undefined ? null : v);
  const hit = STABLE_CACHE.get(v);
  if (hit !== undefined) return hit;
  const out = Array.isArray(v) ? "[" + v.map(stable).join(",") + "]"
    : "{" + Object.keys(v).filter((k) => v[k] !== undefined).sort().map((k) => JSON.stringify(k) + ":" + stable(v[k])).join(",") + "}";
  STABLE_CACHE.set(v, out);
  return out;
}

/* which top-level sections differ between two documents (for "N changes since last publish") */
export const SECTION_LABEL: Record<string, string> = {
  business: "Business", brand: "Design", integrations: "Business", claims: "Business", pages: "Pages & SEO", home: "Home",
  styles: "Styles", areas: "Service area", posts: "Blog", about: "About", financing: "Financing", cost: "Cost",
  repair: "Repair", gates: "Gates", care: "Care", contact: "Contact", refer: "Referrals", reviewAsk: "Reviews", legal: "Legal",
};
export function changedSections(a: any, b: any): string[] {
  if (!a || !b) return [];
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  const out: string[] = [];
  keys.forEach((k) => { if (stable(a[k]) !== stable(b[k])) { const l = SECTION_LABEL[k] || k; if (!out.includes(l)) out.push(l); } });
  return out;
}

export const slugify = (s: string, max = 60) => String(s || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "")
  .replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, max).replace(/-+$/g, "");
/* subdomain rule from the contract: [a-z0-9-]{3,40} */
export const siteSlugOk = (s: string) => /^[a-z0-9](?:[a-z0-9-]{1,38})[a-z0-9]$/.test(s || "");
export function suggestSlug(name: string) {
  const s = slugify(String(name || "").replace(/\b(llc|inc|co|company|corp|ltd)\b\.?/gi, ""), 40);
  return s.length >= 3 ? s : (s + "-fence").replace(/^-/, "");
}

/* "(920) 655-2193" → "+19206552193" (US/CA); anything else passes through with a + */
export function toE164(display: string) {
  const d = String(display || "").replace(/\D/g, "");
  if (d.length === 10) return "+1" + d;
  if (d.length === 11 && d[0] === "1") return "+" + d;
  return d ? "+" + d : "";
}
export function fmtPhone(v: string) {
  const d = String(v || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(v || "");
}

export const fmtDate = (iso: any) => { const t = Date.parse(iso || ""); return t ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""; };
export const fmtDateTime = (iso: any) => { const t = Date.parse(iso || ""); return t ? new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""; };
export const longDate = (isoDay: string) => { const t = Date.parse(isoDay + "T12:00:00"); return t ? new Date(t).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : ""; };

/* ---- design presets (accent, accentDark, ink, cream, heading, body) ---- */
export type Colors = SiteContent["brand"]["colors"];
export const COLOR_PRESETS: { id: string; label: string; colors: Colors }[] = [
  { id: "kodiak", label: "Amber", colors: { accent: "#C6821C", accentDark: "#A96A12", ink: "#15171C", cream: "#F4F2ED", heading: "#1A1D23", body: "#51565E" } },
  { id: "blue", label: "Blue", colors: { accent: "#2F6FDE", accentDark: "#2458B5", ink: "#0F172A", cream: "#F3F5F9", heading: "#111827", body: "#4B5563" } },
  { id: "green", label: "Green", colors: { accent: "#2E8B57", accentDark: "#236B43", ink: "#10231A", cream: "#F2F4EF", heading: "#14201A", body: "#4E5A52" } },
  { id: "red", label: "Red", colors: { accent: "#C0392B", accentDark: "#992D22", ink: "#1B1B1B", cream: "#F5F2EF", heading: "#1C1C1C", body: "#555555" } },
];
export const COLOR_FIELDS: [keyof Colors, string, string][] = [
  ["accent", "Accent", "Buttons, links, highlights"], ["accentDark", "Accent dark", "Button hover, small text on light"],
  ["ink", "Ink", "Header, footer, dark sections"], ["cream", "Background", "Page background"],
  ["heading", "Headings", "Titles"], ["body", "Body text", "Paragraphs"],
];
/* the Print Kit's own colors, when the contractor set them (manifest keys amber/amberDark/ink/cream/slate/slate2) */
export function printKitColors(settings: any): Colors | null {
  const c = settings && settings.printKit && settings.printKit.config && settings.printKit.config.colors;
  if (!c || !(c.amber || c.ink)) return null;
  const base = COLOR_PRESETS[0].colors;
  return { accent: c.amber || base.accent, accentDark: c.amberDark || c.amber || base.accentDark, ink: c.ink || base.ink, cream: c.cream || base.cream, heading: c.slate || base.heading, body: c.slate2 || base.body };
}
export const FONTS: { id: SiteContent["brand"]["font"]; label: string; css: string }[] = [
  { id: "figtree", label: "Figtree — friendly, modern", css: "'Figtree', system-ui, sans-serif" },
  { id: "inter", label: "Inter — clean, neutral", css: "Inter, system-ui, sans-serif" },
  { id: "montserrat", label: "Montserrat — bold, geometric", css: "Montserrat, system-ui, sans-serif" },
  { id: "barlow", label: "Barlow — rugged, condensed feel", css: "Barlow, 'Arial Narrow', system-ui, sans-serif" },
];
export const fontCss = (id: string) => (FONTS.find((f) => f.id === id) || FONTS[0]).css;

/* ---- the styles a contractor can offer (wizard + generator slugs) ---- */
export const STYLE_CHOICES: { slug: string; name: string; hint: string }[] = [
  { slug: "vinyl", name: "Vinyl / PVC", hint: "Privacy, semi-privacy, picket" },
  { slug: "wood-privacy", name: "Wood privacy", hint: "Cedar, pine, pressure-treated" },
  { slug: "chain-link", name: "Chain link", hint: "Galvanized & black vinyl-coated" },
  { slug: "aluminum", name: "Aluminum / ornamental", hint: "Black powder-coat, decorative" },
  { slug: "pool", name: "Pool fencing", hint: "Code-compliant barriers" },
];
/* price-book categories → style slugs (the wizard's pre-ticks) */
export function stylesFromCatalog(products: any[]): string[] {
  const cats = new Set((products || []).map((p: any) => String((p && p.category) || "").toLowerCase()));
  const out: string[] = [];
  if ([...cats].some((c) => c.includes("vinyl"))) out.push("vinyl");
  if ([...cats].some((c) => c.includes("wood") || c.includes("cedar"))) out.push("wood-privacy");
  if ([...cats].some((c) => c.includes("chain"))) out.push("chain-link");
  if ([...cats].some((c) => c.includes("alum") || c.includes("iron") || c.includes("ornamental"))) out.push("aluminum", "pool");
  return out;
}

/* ---- pages ---- */
export const PAGE_LIST: { key: PageKey; label: string; path: string; locked?: string }[] = [
  { key: "home", label: "Home", path: "/", locked: "Your home page is always on." },
  { key: "styles", label: "Fence styles", path: "/styles" },
  { key: "locations", label: "Service area & towns", path: "/locations" },
  { key: "about", label: "About", path: "/about" },
  { key: "quote", label: "Get a quote", path: "/quote", locked: "The quote form is how your site turns visitors into leads." },
  { key: "contact", label: "Contact", path: "/contact" },
  { key: "reviews", label: "Reviews", path: "/reviews" },
  { key: "review", label: "Leave a review", path: "/review" },
  { key: "financing", label: "Financing", path: "/financing" },
  { key: "cost", label: "Fence cost guide", path: "/cost" },
  { key: "repair", label: "Fence repair", path: "/fence-repair" },
  { key: "gates", label: "Gates & openers", path: "/gates" },
  { key: "care", label: "Fence care", path: "/care" },
  { key: "refer", label: "Refer a neighbor", path: "/refer" },
  { key: "blog", label: "Blog", path: "/blog" },
  { key: "privacy", label: "Privacy policy", path: "/privacy", locked: "Required — your forms collect contact details." },
  { key: "terms", label: "Terms", path: "/terms", locked: "Required alongside the privacy policy." },
];
/* which site path the preview shows for an editor tab (site app routes, Kodiak design) */
export function previewPath(tab: string, sub?: string | null): string {
  switch (tab) {
    case "styles": return sub ? "/styles/" + sub : "/styles";
    case "areas": return sub ? "/locations/" + sub : "/locations";
    case "about": return "/about";
    case "financing": return "/financing";
    case "repair": return "/fence-repair";
    case "care": return "/care";
    case "contact": return "/contact";
    case "blog": return sub ? "/blog/" + sub : "/blog";
    default: return "/";
  }
}

/* ---- hosts ---- */
export function subdomainHost(b: SiteBundle | null) {
  if (!b || !b.site) return "";
  const sub = (b.domains || []).find((d) => d.kind === "subdomain" && d.status !== "removed");
  return sub ? sub.hostname : b.site.slug + "." + (b.rootDomain || "fenceworks.site");
}
export function primaryHost(b: SiteBundle | null) {
  if (!b || !b.site) return "";
  const p = (b.domains || []).find((d) => d.is_primary && d.status === "active");
  return p ? p.hostname : subdomainHost(b);
}
export function liveUrlOf(b: SiteBundle | null) {
  if (!b || !b.site) return "";
  return b.liveUrl || "https://" + primaryHost(b);
}
/* the preview URL for a path: the API's previewUrl with its path swapped, else
   https://{slug}.{root}{path}?__preview={token} */
export function previewUrlFor(b: SiteBundle | null, path: string) {
  if (!b || !b.site) return "";
  const token = b.site.preview_token || "";
  try {
    if (b.previewUrl) { const u = new URL(b.previewUrl); u.pathname = path || "/"; if (token && !u.searchParams.get("__preview")) u.searchParams.set("__preview", token); return u.toString(); }
  } catch (e) { /* fall through */ }
  return "https://" + subdomainHost(b) + (path || "/") + "?__preview=" + encodeURIComponent(token);
}
export function recordsOf(d: DomainRow): DnsRecord[] {
  const v: any = d && d.verification;
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.records)) return v.records;
  return [];
}
export function apexOf(host: string) { return String(host || "").replace(/^www\./, ""); }
export function cleanHostname(v: string) {
  return String(v || "").trim().toLowerCase().replace(/^[a-z]+:\/\//, "").replace(/[/?#].*$/, "").replace(/:\d+$/, "").replace(/\.$/, "");
}

/* ---- tolerate a document with a missing collection ----
   Fills ONLY what's missing (never overwrites), and keeps object identity
   where nothing was filled — so a complete document comes back unchanged
   (same objects, same stable() strings, no false "changes"). */
const P0 = { src: "", alt: "" };
const SK: any = {
  business: { name: "", shortName: "", phone: { display: "", e164: "" }, email: "", address: { city: "", region: "", showStreet: false }, hoursText: "", hours: [], serviceAreaText: "", insured: false },
  brand: { logo: {}, colors: { ...COLOR_PRESETS[0].colors }, font: "figtree" },
  integrations: { widget: "both", social: {} },
  claims: { permitsHandled: false, locateService: "811" },
  pages: {},
  home: { eyebrow: "", h1: "", lead: "", heroPhoto: P0, whyTitle: "", why: [], process: [], servicesTitle: "", areaTitle: "", areaBlurb: "" },
  styles: { items: [], guide: [], compare: [], faqs: [] },
  areas: { intro: "", radiusText: "", regions: [], cities: [], faqs: [] },
  posts: [],
  about: { h1: "", story: [], stats: [], neverCut: [], standard: [], faqs: [] },
  financing: { why: [], steps: [], covered: [], faqs: [], calculator: { apr: 0, terms: [], min: 0, max: 0, start: 0 } },
  cost: { intro: "", factors: [], samples: [] },
  repair: { intro: "", repairs: [], process: [], faqs: [] },
  gates: { intro: "", types: [], operators: [], faqs: [] },
  care: { rules: [], materials: [], seasons: [], faqs: [] },
  contact: { nextSteps: [], faqs: [] },
  refer: { steps: [], promises: [] },
  reviewAsk: { steps: [] },
  legal: { governingState: "", lastUpdated: "" },
};
const STYLE_SK: any = { slug: "", name: "", on: true, tag: "", heights: "", thumb: P0, heroPhoto: P0, gallery: [], tagline: "", intro: "", features: [], materials: [], bestFor: [], overview: [], process: [], climate: [], climateTitle: "", costFactors: [], care: [], faqs: [], price: "" };
const CITY_SK: any = { slug: "", name: "", on: true, county: "", drive: "", popular: [], tagline: "", intro: [], why: "", neighborhoods: [], faqs: [] };
const POST_SK: any = { slug: "", title: "", category: "Planning", date: "", dateISO: "", read: 4, excerpt: "", body: [] };
const REGION_SK: any = { name: "", blurb: "", cities: [] };
const MAT_SK: any = { name: "", points: [] };
function fill(v: any, sk: any): any {
  if (Array.isArray(sk)) return Array.isArray(v) ? v : [];
  if (sk && typeof sk === "object") {
    const base = v && typeof v === "object" && !Array.isArray(v) ? v : null;
    let out = base || {};
    let changed = !base;
    Object.keys(sk).forEach((k) => { const nv = fill(out[k], sk[k]); if (nv !== out[k]) { if (!changed) { out = { ...out }; changed = true; } out[k] = nv; } });
    return out;
  }
  return v === undefined || v === null ? sk : v;
}
const fillEach = (arr: any[], sk: any) => { let ch = false; const out = arr.map((x) => { const y = fill(x, sk); if (y !== x) ch = true; return y; }); return ch ? out : arr; };
export function normalizeContent<T = SiteContent>(d: any): T {
  if (!d || typeof d !== "object") return d;
  let out = fill(d, SK);
  const each = (path: string, sk: any) => { const a = getIn(out, path); const b = fillEach(a, sk); if (b !== a) out = setIn(out, path, b); };
  each("styles.items", STYLE_SK); each("areas.cities", CITY_SK); each("areas.regions", REGION_SK); each("posts", POST_SK); each("care.materials", MAT_SK);
  return out;
}
